import {
  FieldValue,
  Option,
  RadioGroupField,
} from '../../../components/AutoForm/AutoFieldNew';
import {SetFieldValueWithSourceKey} from '../../../components/AutoForm/useAutoForm';
import OWNERSHIP from '../../../constants/Ownership';
import {AccountBasicFragment} from '../../../schema-types/AccountBasicFragment';
import {
  OBJECTIVE_CAPITAL,
  OBJECTIVE_INCOME,
  OWNER_FAMILY,
  OWNER_JOINT,
  OWNER_PRIMARY,
  OWNER_SECONDARY,
  OWNER_TRUST,
} from './constants';
import {UPDATES} from '../../../shared/UPDATES';
import {GoalFormValues, getGoalFormName, getOwnerName} from './utils';

export const setOwnershipRadio = ({
  setFieldValue,
  values,
  account,
}: {
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
  values: Pick<GoalFormValues, 'ownershipRadio' | 'ownership' | 'contact'>;
  account?: AccountBasicFragment;
}) => {
  if (values.ownershipRadio) {
    return;
  }

  const {ownership} = values;

  if (ownership === OWNERSHIP.ACCOUNT_OWNED) {
    setFieldValue(
      UPDATES._1100,
      'ownershipRadio',
      account?.isTrust ? OWNER_TRUST : OWNER_FAMILY
    );
  } else if (ownership === OWNERSHIP.JOINT_LIVES) {
    setFieldValue(UPDATES._1100, 'ownershipRadio', OWNER_JOINT);
  } else if (ownership === OWNERSHIP.TRUST) {
    setFieldValue(UPDATES._1100, 'ownershipRadio', OWNER_TRUST);
  } else {
    const savedContact = account?.contacts?.find((c) => c?.id === values.contact?.id);
    setFieldValue(
      UPDATES._1100,
      'ownershipRadio',
      savedContact?.accountContacts.find((ac) => ac.account?.id === account?.id)
        ?.isSecondary
        ? OWNER_SECONDARY
        : OWNER_PRIMARY
    );
  }
};

export const validateGoalReasons = ({
  values,
  hasPlanningObjectivePurpose,
  setRequireValidation,
}: {
  values: GoalFormValues;
  hasPlanningObjectivePurpose: boolean;
  setRequireValidation: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const newValidationArray: string[] = [];

  if (!values.ownership) {
    newValidationArray.push('Who is the goal for?');
  }
  if (!values.planningObjective) {
    newValidationArray.push('What does the client want?');
  }
  if (hasPlanningObjectivePurpose && !values.planningObjectivePurpose) {
    newValidationArray.push("What's the purpose of the income?");
  }
  if (!values.description) {
    newValidationArray.push('Please add the Goal description');
  }

  setRequireValidation(newValidationArray);
};

export const populateOwnershipRadioButtons = ({
  account,
  ownershipRadioField,
  setOwnershipOptions,
}: {
  account: AccountBasicFragment | undefined;
  ownershipRadioField: RadioGroupField;
  setOwnershipOptions: React.Dispatch<React.SetStateAction<Option<FieldValue>[]>>;
}) => {
  let newOwnershipOptions = [...ownershipRadioField.options];

  if (account?.isTrust) {
    newOwnershipOptions = [
      {
        value: OWNER_TRUST,
        label: getOwnerName(OWNER_TRUST, account),
      },
    ];
    setOwnershipOptions(newOwnershipOptions);
    return;
  }

  if (account?.contacts) {
    if (account?.contacts.length > 0) {
      newOwnershipOptions = [
        {
          value: OWNER_PRIMARY,
          label: getOwnerName(OWNER_PRIMARY, account),
        },
      ];
    }
    if (account?.contacts.length > 1) {
      newOwnershipOptions.push({
        value: OWNER_SECONDARY,
        label: getOwnerName(OWNER_SECONDARY, account),
      });
      newOwnershipOptions.push({
        value: OWNER_JOINT,
        label: getOwnerName(OWNER_JOINT, account),
      });
      newOwnershipOptions.push({
        value: OWNER_FAMILY,
        label: getOwnerName(OWNER_FAMILY, account),
      });
    }
    setOwnershipOptions(newOwnershipOptions);
  }
};

export const setGoalOwner = ({
  ownershipRadio,
  setFieldValue,
  account,
}: {
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
  ownershipRadio: GoalFormValues['ownershipRadio'];
  account?: AccountBasicFragment;
}) => {
  if (account?.isTrust) {
    setFieldValue(UPDATES._1101, 'ownership', OWNERSHIP.ACCOUNT_OWNED);
  } else {
    const primaryContact = account?.contacts?.find((c) =>
      c.accountContacts.find((ac) => ac.account.id === account.id && ac.isPrimary)
    );
    const secondaryContact = account?.contacts?.find((c) =>
      c.accountContacts.find((ac) => ac.account.id === account.id && ac.isSecondary)
    );
    switch (ownershipRadio) {
      case OWNER_PRIMARY:
        setFieldValue(UPDATES._1101, 'ownership', OWNERSHIP.SINGLE_LIVES);
        setFieldValue(UPDATES._1102, 'contactId', primaryContact?.id);
        break;
      case OWNER_SECONDARY:
        setFieldValue(UPDATES._1101, 'ownership', OWNERSHIP.SINGLE_LIVES);
        // passing secondary owner as the primary contact of a Single life ownership goal
        // to prevent bugs in recommendations
        setFieldValue(UPDATES._1102, 'contactId', secondaryContact?.id);
        break;
      case OWNER_JOINT:
        setFieldValue(UPDATES._1101, 'ownership', OWNERSHIP.JOINT_LIVES);
        setFieldValue(UPDATES._1102, 'contactId', primaryContact?.id);
        setFieldValue(UPDATES._1103, 'secondaryContactId', secondaryContact?.id);
        break;
      case OWNER_FAMILY:
        setFieldValue(UPDATES._1101, 'ownership', OWNERSHIP.ACCOUNT_OWNED);
        break;
      default:
      // throw Error('No Ownership radio button selected');
    }
  }

  const ownerName = getOwnerName(ownershipRadio, account);
  setFieldValue(UPDATES._1104, 'ownerName', ownerName);
};

export const setGoalType = ({
  setHasPlanningObjectivePurpose,
  setFieldValue,
  values,
  setIsFullGoalSteps,
  setPlanningObjectivePurpose,
  planningObjectivePurposeField,
}: {
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
  values: GoalFormValues;
  setHasPlanningObjectivePurpose: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFullGoalSteps: React.Dispatch<React.SetStateAction<boolean>>;
  setPlanningObjectivePurpose: React.Dispatch<React.SetStateAction<RadioGroupField>>;
  planningObjectivePurposeField: RadioGroupField;
}) => {
  if (!values.planningObjective) {
    return;
  }
  const hasPlanningObjectivePurpose =
    values.planningObjective === OBJECTIVE_CAPITAL ||
    values.planningObjective === OBJECTIVE_INCOME;
  setHasPlanningObjectivePurpose(hasPlanningObjectivePurpose);
  if (hasPlanningObjectivePurpose) {
    setFieldValue(
      UPDATES._1105,
      'goalType',
      values.planningObjective === OBJECTIVE_INCOME ? OBJECTIVE_INCOME : OBJECTIVE_CAPITAL
    );
  }
  setIsFullGoalSteps(hasPlanningObjectivePurpose);

  const newPOPurpose = {...planningObjectivePurposeField};
  newPOPurpose.label = `Purpose of ${
    values.planningObjective === OBJECTIVE_INCOME ? 'Income' : 'Pot'
  } is:`;
  setPlanningObjectivePurpose({...newPOPurpose});
};

export const updateGoalFormName = ({
  setFieldValue,
  setGoalFormName,
  values,
}: {
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
  values: GoalFormValues;
  setGoalFormName: (x: string) => void;
}) => {
  const name = getGoalFormName(values);
  setFieldValue(UPDATES._1106, 'name', name);
  setGoalFormName(`Goal Editor: ${name}`);
};
